/**
 * Updates on template rows: the stored shape, counting, and the serializer both
 * apply paths use.
 *
 * Stored on a row:  updates: [{ body, mentions: [{ id, type, name }] }]
 *   body      plain text with line breaks; a mention appears in it as "@Name"
 *   mentions  who each "@Name" refers to — type 'User' | 'Team', or 'Applier'
 *             for the dynamic mention, which appears in the body as "@Applier"
 *             and resolves to whoever applies the template
 *
 * A mention is tied to the body by its text, not by a position or a token: a
 * mention whose "@Name" is no longer in the body is simply not sent. That keeps
 * the body an ordinary editable string that survives any edit.
 *
 * Sent as HTML (see serializeUpdate): the text escaped, line breaks as <br>,
 * each mention as monday's own mention anchor.
 *
 * Mirrored in spt/src/helpers/updates.ts, fixture for fixture.
 */
export const APPLIER_NAME = 'Applier';
export const APPLIER_TEXT = `@${APPLIER_NAME}`;
export const APPLIER_MENTION = Object.freeze({ type: 'Applier', name: APPLIER_NAME });

export const emptyUpdate = () => ({ body: '', mentions: [] });

/** Total updates across rows (the card and footer counts). */
export function countUpdates(rows) {
  return (rows || []).reduce((n, r) => n + ((r && r.updates) || []).length, 0);
}

/** The text a mention appears as in the body. */
export function mentionText(mention) {
  return `@${mention.type === 'Applier' ? APPLIER_NAME : mention.name}`;
}

export const mentionKey = (mention) => (mention.type === 'Applier' ? 'Applier' : `${mention.type}:${mention.id}`);

export function mentionIsUsed(body, mention) {
  return String(body || '').includes(mentionText(mention));
}

/** Adds a mention once; the body is left to the caller (it inserts the "@Name" text). */
export function withMention(update, mention) {
  const mentions = update.mentions || [];
  if (mentions.some((m) => mentionKey(m) === mentionKey(mention))) return update;
  return { ...update, mentions: [...mentions, { ...mention }] };
}

/** Drops mentions whose "@Name" left the body. Run after every body edit. */
export function pruneMentions(update) {
  const mentions = (update.mentions || []).filter((m) => m && mentionIsUsed(update.body, m));
  return mentions.length === (update.mentions || []).length ? update : { ...update, mentions };
}

/**
 * How a mention reaches monday. Probed on 2026-10-14 (API 2025-07 and 2026-01):
 *   - for every entry in mentions_list monday APPENDS its own mention anchor to
 *     the end of the body, even when the same anchor is already inline;
 *   - monday's own anchor markup in the body, with NO mentions_list, stays
 *     where it is and notifies the person (checked with a second member).
 *   'appended'  "@Name" stays as the plain name and mentions_list makes monday
 *               add the mentions at the end. The default since 2026-10-19:
 *               inline anchors stopped notifying on staging, and
 *               mentions_list is the only mention monday documents
 *               (create_update, API 2025-07+).
 *   'inline'    the "@Name" text becomes monday's own mention anchor where it
 *               was typed and mentions_list stays empty. Keeps the name in
 *               place but is undocumented, and was seen not to notify.
 * Mirrored in spt/src/helpers/updates.ts.
 */
export const MENTION_STYLE = 'appended';

const escapeHtml = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Plain template text as update HTML: escaped, line breaks kept. */
const textHtml = (text) => escapeHtml(text).replace(/\r?\n/g, '<br>');

/**
 * The anchor monday itself stores for a mention (copied from a probed update).
 * `slug` is the account's subdomain; without it the link is account-relative.
 */
export function mentionMarkup({ id, type, name }, slug) {
  const path = type === 'Team' ? 'teams' : 'users';
  const href = slug ? `https://${slug}.monday.com/${path}/${id}` : `/${path}/${id}`;
  return `<a class="user_mention_editor router" href="${href}" data-mention-type="${type}" data-mention-id="${id}" target="_blank" rel="noopener noreferrer">@${escapeHtml(name)}</a>`;
}

/**
 * Body (HTML) and mentions_list for create_update.
 *
 * @param ctx.applier { id, name } of the person applying, or null — then
 *                    "@Applier" stays as plain text and nobody is notified.
 * @param ctx.known   Set of "User:123" / "Team:45" keys that exist in the
 *                    account, or null to skip the check. A mention that is not
 *                    known is dropped from mentions_list and its "@Name" text
 *                    stays (F1.12: a user who left must not fail the update).
 * @param ctx.slug    the account's subdomain, for the mention links.
 * @param ctx.style   MENTION_STYLE unless a test says otherwise.
 */
export function serializeUpdate(update, { applier = null, known = null, slug = null, style = MENTION_STYLE } = {}) {
  const raw = String((update && update.body) || '');
  // Every mention that will be sent, with the text that marks it in the body.
  const sendable = [];
  const seen = new Set();
  for (const m of (update && update.mentions) || []) {
    if (!m) continue;
    let target;
    if (m.type === 'Applier') {
      if (!applier) continue;
      target = { id: applier.id, type: 'User', name: applier.name };
    } else {
      if (known && !known.has(mentionKey(m))) continue;
      target = { id: m.id, type: m.type, name: m.name };
    }
    const text = mentionText(m);
    if (!raw.includes(text)) continue;
    sendable.push({ text, target });
  }

  let body = '';
  for (const seg of splitOn(raw, sendable)) {
    if (seg.kind === 'text') body += textHtml(seg.text);
    else if (style === 'inline') body += mentionMarkup(seg.entry.target, slug);
    else body += escapeHtml(seg.entry.target.name); // monday appends the mention itself
  }

  // Inline, the anchors ARE the mentions; a list would make monday append each again.
  if (style === 'inline') return { body, mentions_list: [] };

  const mentions_list = [];
  for (const { target } of sendable) {
    const key = `${target.type}:${target.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    mentions_list.push({ id: target.id, type: target.type });
  }
  return { body, mentions_list };
}

/**
 * The body cut into text and `entry` segments, where an entry is anything
 * with a `text` it appears as. Longer texts are matched first so "@Ann" never
 * claims the start of "@Annabel".
 */
function splitOn(body, entries) {
  const names = entries.filter((n) => n.text.length > 1).sort((a, b) => b.text.length - a.text.length);
  if (!names.length || !body) return body ? [{ kind: 'text', text: body }] : [];
  const startsHere = (i) => names.find((n) => body.startsWith(n.text, i));
  const segments = [];
  let i = 0;
  while (i < body.length) {
    const hit = startsHere(i);
    if (hit) {
      segments.push({ kind: 'entry', text: hit.text, entry: hit });
      i += hit.text.length;
      continue;
    }
    let j = i + 1;
    while (j < body.length && !startsHere(j)) j++;
    segments.push({ kind: 'text', text: body.slice(i, j) });
    i = j;
  }
  return segments;
}

/** The body split into text and mention segments, for rendering chips. */
export function segmentsOf(update) {
  const body = String((update && update.body) || '');
  const entries = ((update && update.mentions) || []).filter(Boolean).map((m) => ({ text: mentionText(m), mention: m }));
  return splitOn(body, entries).map((s) => (s.kind === 'entry' ? { kind: 'mention', text: s.text, mention: s.entry.mention } : s));
}

/** The mutation both apply paths send; mentions_list needs API 2025-07 or later (the pin is 2026-01). */
export const CREATE_UPDATE_MUTATION = `mutation ($itemId: ID!, $body: String!, $mentions: [UpdateMention]) {
  create_update(item_id: $itemId, body: $body, mentions_list: $mentions) { id }
}`;
